import React from "react";
import {
  ArrowRight,
  CalendarClock,
  GitPullRequest,
  ShieldAlert,
} from "lucide-react";
import type {
  AssuranceAssessment,
  AssuranceFinding,
  AssuranceRepository,
} from "../../../assurance/types";
import {
  isEvidenceBacked,
  selectCapabilities,
  selectPriorityQueue,
  selectScanDelta,
  selectDecision,
  selectDependencies,
  selectFindingsSummary,
  selectPolicies,
  selectQuality,
  selectScan,
  selectSyntheticBoundary,
} from "../../../assurance/model";
import { SeverityBadge, type Severity } from "../../ui/SeverityBadge";
import { cn } from "../../../lib/utils";
import {
  BarList,
  CaveatNote,
  EmptyPanel,
  FindingLink,
  MetricTile,
  NotMeasured,
  Pill,
  SectionPanel,
  StatusPill,
  fmtDateTime,
  fmtNumber,
  fmtPercent,
  freshnessTone,
  relativeTime,
  statusTone,
  ViewEvidenceButton,
} from "../primitives";

export const SEVERITY_ORDER: Severity[] = ["critical", "high", "medium", "low", "info"];

export const SEVERITY_BG: Record<Severity, string> = {
  critical: "bg-critical",
  high: "bg-high",
  medium: "bg-medium",
  low: "bg-low",
  info: "bg-info",
};

interface ExecutiveViewProps {
  assessment: AssuranceAssessment;
  demo: boolean;
  onSelectView: (view: string, params?: Record<string, string>) => void;
  onSelectFinding: (finding: AssuranceFinding, openEvidence?: boolean) => void;
}

// ---------------------------------------------------------------------------
// Score dial
// ---------------------------------------------------------------------------

function scoreBand(score: number): { label: string; color: string; arc: string } {
  if (score >= 80) return { label: "Critical risk", color: "text-critical", arc: "rgb(var(--critical))" };
  if (score >= 60) return { label: "High risk", color: "text-high", arc: "rgb(var(--high))" };
  if (score >= 40) return { label: "Moderate risk", color: "text-medium", arc: "rgb(var(--medium))" };
  if (score >= 20) return { label: "Low risk", color: "text-low", arc: "rgb(var(--low))" };
  return { label: "Healthy", color: "text-success", arc: "rgb(var(--success))" };
}

function RiskDial({ score, band }: { score: number; band?: string }) {
  const arcLength = 157;
  const clamped = Math.max(0, Math.min(100, score));
  const derived = scoreBand(clamped);
  return (
    <div className="flex items-center justify-center">
      <svg viewBox="0 0 120 72" className="w-full max-w-[220px]" role="img" aria-label={`Contextual risk score ${clamped} of 100 — ${band || derived.label}`}>
        <path
          d="M10,62 A50,50 0 0 1 110,62"
          fill="none"
          stroke="rgb(var(--border) / 0.5)"
          strokeWidth="8"
          strokeLinecap="round"
        />
        <path
          d="M10,62 A50,50 0 0 1 110,62"
          fill="none"
          stroke={derived.arc}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={arcLength}
          strokeDashoffset={arcLength - (clamped / 100) * arcLength}
          className="transition-all duration-700 ease-out"
        />
        <text x="60" y="48" textAnchor="middle" className="fill-text font-bold" style={{ fontSize: "26px" }}>
          {clamped}
        </text>
        <text x="60" y="64" textAnchor="middle" className="fill-muted" style={{ fontSize: "9px", fontWeight: 600 }}>
          {band || derived.label}
        </text>
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Application assurance (executive posture)
// ---------------------------------------------------------------------------

export function ApplicationView({
  assessment,
  demo,
  onSelectView,
  onSelectFinding,
}: ExecutiveViewProps) {
  const summary = selectFindingsSummary(assessment);
  const scan = selectScan(assessment);
  const decision = selectDecision(assessment);
  const policies = selectPolicies(assessment);
  const dependencies = selectDependencies(assessment);
  const quality = selectQuality(assessment);
  const capabilities = selectCapabilities(assessment);
  const priorityQueue = selectPriorityQueue(assessment);
  const scanDelta = selectScanDelta(assessment);

  const failingPolicies = policies.filter(
    (policy) => statusTone(policy.status) === "critical"
  );
  const priorityFindings = assessment.findings
    .filter((finding) => finding.priorityEvidence)
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
    .slice(0, 6);
  const severityItems = SEVERITY_ORDER.filter(
    (severity) => (summary.bySeverity[severity] ?? summary.bySeverity[severity[0].toUpperCase() + severity.slice(1)]) !== undefined
  ).map((severity) => {
    const count =
      summary.bySeverity[severity] ??
      summary.bySeverity[severity[0].toUpperCase() + severity.slice(1)] ??
      0;
    return { severity, count };
  });

  const decisionCopy: Record<string, { label: string; tone: "critical" | "warning" | "success" | "neutral"; detail: string }> = {
    blocked: {
      label: "Release blocked",
      tone: "critical",
      detail: `${decision.failing} of ${decision.total} policy gates failing`,
    },
    review: {
      label: "Conditional — review required",
      tone: "warning",
      detail: `${decision.review} of ${decision.total} policy gates need review`,
    },
    pass: {
      label: "Policy gates passing",
      tone: "success",
      detail: `${decision.passing} of ${decision.total} policy gates satisfied`,
    },
    unknown: {
      label: decision.total
        ? "Gates require validation"
        : demo
          ? "Requires validation"
          : "Not measured",
      tone: "neutral",
      detail: decision.total
        ? `${decision.total} policy gates require validation against production thresholds before a release decision.`
        : "No policy evaluations were returned for this assessment.",
    },
  };
  const decisionInfo = decisionCopy[decision.state];

  const evidenceBacked = capabilities.filter((row) => isEvidenceBacked(row.status)).length;

  return (
    <div className="space-y-4">
      {/* Posture row */}
      <div className="grid gap-4 xl:grid-cols-3">
        <SectionPanel
          title="Contextual risk"
          description={summary.riskScore !== undefined ? "Prototype-weighted score across measured findings" : undefined}
        >
          {summary.riskScore !== undefined ? (
            <RiskDial score={summary.riskScore} band={summary.riskBand} />
          ) : (
            <EmptyPanel demo={demo} title={undefined} detail="No risk score was computed for this assessment." />
          )}
        </SectionPanel>

        <SectionPanel
          title="Release decision"
          description="Derived from PR-guard policy gates"
          actions={
            <button
              type="button"
              onClick={() => onSelectView("pr-guard")}
              className="btn-ghost px-2 py-1 text-xs"
            >
              PR guard
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          }
        >
          <div className="flex flex-col gap-3">
            <Pill tone={decisionInfo.tone} icon={<GitPullRequest className="h-3.5 w-3.5" aria-hidden="true" />} className="self-start px-3 py-1.5 text-sm">
              {decisionInfo.label}
            </Pill>
            <p className="text-xs leading-5 text-muted">{decisionInfo.detail}</p>
            {failingPolicies.length > 0 && (
              <ul className="space-y-1.5">
                {failingPolicies.slice(0, 3).map((policy) => (
                  <li key={policy.id || policy.name} className="flex items-start gap-2 text-xs leading-5 text-text2">
                    <ShieldAlert className="mt-0.5 h-3.5 w-3.5 flex-none text-critical" aria-hidden="true" />
                    <span>
                      <span className="font-semibold text-text">{policy.name}</span>
                      {policy.evidence && <span className="text-muted"> — {policy.evidence}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </SectionPanel>

        <SectionPanel
          title="Open exposure"
          description="Open findings by severity"
          actions={
            <button
              type="button"
              onClick={() => onSelectView("findings")}
              className="btn-ghost px-2 py-1 text-xs"
            >
              Inventory
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          }
        >
          {severityItems.length ? (
            <BarList
              ariaLabel="Open findings by severity"
              items={severityItems.map(({ severity, count }) => ({
                label: <SeverityBadge severity={severity} size="sm" />,
                value: count,
                colorClass: SEVERITY_BG[severity],
                onClick: () => onSelectView("findings", { severity }),
              }))}
            />
          ) : (
            <EmptyPanel demo={demo} detail="No severity summary was returned." />
          )}
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-divider/70 pt-2.5 text-xs text-muted">
            <span>
              Priority evidence{" "}
              <span className="font-semibold tabular-nums text-text">
                {fmtNumber(summary.priorityEvidenceCount) ?? "—"}
              </span>
            </span>
            <span>
              Est. reachable{" "}
              <span className="font-semibold tabular-nums text-text">
                {fmtNumber(summary.estimatedReachable) ?? "—"}
              </span>
            </span>
          </div>
        </SectionPanel>
      </div>

      {/* Cross-domain tiles */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <MetricTile
          label="Total findings"
          value={fmtNumber(summary.total)}
          tone="neutral"
          demo={demo}
        />
        <MetricTile
          label="Critical open"
          value={fmtNumber(
            summary.bySeverity.critical ?? summary.bySeverity.Critical
          )}
          tone={(summary.bySeverity.critical ?? summary.bySeverity.Critical ?? 0) > 0 ? "critical" : "success"}
          demo={demo}
        />
        <MetricTile
          label="Scan coverage"
          value={fmtPercent(scan.coveragePercent)}
          sub={
            scan.filesAnalyzed !== undefined
              ? `${fmtNumber(scan.filesAnalyzed)} files analyzed`
              : undefined
          }
          demo={demo}
        />
        <MetricTile
          label="Components"
          value={fmtNumber(dependencies.components)}
          sub={
            dependencies.vulnerable !== undefined
              ? `${fmtNumber(dependencies.vulnerable)} vulnerable`
              : undefined
          }
          tone={dependencies.vulnerable ? "warning" : "neutral"}
          demo={demo}
        />
        <MetricTile
          label="Debt estimate"
          value={
            quality.estimatedDebtDays !== undefined
              ? `${fmtNumber(quality.estimatedDebtDays)} d`
              : undefined
          }
          sub={fmtPercent(quality.debtRatioPercent) && `${fmtPercent(quality.debtRatioPercent)} debt ratio`}
          demo={demo}
        />
        <MetricTile
          label="Capabilities"
          value={
            capabilities.length
              ? `${evidenceBacked}/${capabilities.length}`
              : undefined
          }
          sub="evidence-backed"
          tone={capabilities.length && evidenceBacked === capabilities.length ? "success" : "neutral"}
          demo={demo}
        />
      </div>

      {/* What changed since the previous scan */}
      {scanDelta && scanDelta.status === "measured" && (
        <SectionPanel
          title="Since the previous scan"
          description={
            scanDelta.previousScanAt
              ? `Compared against the scan of ${fmtDateTime(scanDelta.previousScanAt)}`
              : "Compared against the previous scan"
          }
        >
          <div className="grid gap-3 sm:grid-cols-3">
            <MetricTile
              label="Newly introduced"
              value={fmtNumber(scanDelta.introduced)}
              tone={scanDelta.introduced > 0 ? "critical" : "success"}
              demo={demo}
            />
            <MetricTile
              label="No longer detected"
              value={fmtNumber(scanDelta.resolved)}
              tone={scanDelta.resolved > 0 ? "success" : "neutral"}
              demo={demo}
            />
            <MetricTile
              label="Carried over"
              value={fmtNumber(scanDelta.carriedOver)}
              tone="neutral"
              demo={demo}
            />
          </div>
          <CaveatNote>
            Measured as the difference between the two scans' recorded finding sets, so a finding that was fixed and
            later reintroduced counts as newly introduced. "No longer detected" means absent from this scan, which is
            not by itself proof of remediation.
          </CaveatNote>
        </SectionPanel>
      )}

      {/* Fix these first — ranked by demonstrated exploit signal, with the
          factors that produced each position shown inline. */}
      {priorityQueue.length > 0 && (
        <SectionPanel
          title="Fix these first"
          description="Ranked by demonstrated exploit signal — known-exploited status, exploit probability, and measured exposure — not severity alone"
          actions={
            <button
              type="button"
              onClick={() => onSelectView("findings")}
              className="btn-ghost px-2 py-1 text-xs"
            >
              All findings
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          }
        >
          <ol className="space-y-2">
            {priorityQueue.slice(0, 5).map((entry, index) => {
              const finding = assessment.findings.find((item) => item.id === entry.findingId);
              return (
                <li key={entry.findingId}>
                  <button
                    type="button"
                    onClick={() => finding && onSelectFinding(finding, true)}
                    disabled={!finding}
                    className="flex w-full items-start gap-3 rounded-xl border border-border/70 bg-surface1 p-3 text-left transition-colors hover:border-brand/40 hover:bg-surface2/50 disabled:cursor-default"
                  >
                    <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-brand/12 text-[12px] font-bold text-brand ring-1 ring-brand/20 tabular-nums">
                      {index + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <SeverityBadge severity={entry.severity as Severity} size="sm" />
                        <span className="truncate text-[13.5px] font-semibold text-text">{entry.title}</span>
                      </span>
                      <span className="mt-1.5 flex flex-wrap gap-1.5">
                        {entry.factors
                          .filter((factor) => factor.weight > 0)
                          .map((factor) => (
                            <span
                              key={factor.label}
                              title={factor.detail}
                              className="rounded-full bg-surface2 px-2 py-0.5 text-[11px] font-medium text-text2 ring-1 ring-border/70"
                            >
                              {factor.label}
                            </span>
                          ))}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
          <CaveatNote>
            Ordering combines CISA KEV membership, EPSS exploit probability, measured exposure, severity, and cross-engine
            corroboration. Every factor shown is measured; hover a chip for its basis.
          </CaveatNote>
        </SectionPanel>
      )}

      {/* Priority findings */}
      <SectionPanel
        title="Priority evidence findings"
        description="Highest-scored findings with a demonstrated evidence path"
        flush
        actions={
          <button
            type="button"
            onClick={() => onSelectView("remediation")}
            className="btn-ghost px-2 py-1 text-xs"
          >
            Remediation plan
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        }
      >
        {priorityFindings.length ? (
          <div className="overflow-x-auto">
            <table className="asr-table">
              <thead>
                <tr>
                  <th scope="col">Severity</th>
                  <th scope="col">Finding</th>
                  <th scope="col">Domain</th>
                  <th scope="col">Owner</th>
                  <th scope="col">SLA</th>
                  <th scope="col" className="text-right">Score</th>
                  <th scope="col"><span className="sr-only">Open evidence</span></th>
                </tr>
              </thead>
              <tbody>
                {priorityFindings.map((finding) => (
                  <tr
                    key={finding.id}
                    className="cursor-pointer"
                    onClick={() => onSelectFinding(finding, true)}
                  >
                    <td className="whitespace-nowrap">
                      <SeverityBadge severity={finding.severity} size="sm" />
                    </td>
                    <td className="min-w-[280px] max-w-md">
                      <div className="font-medium text-text">{finding.title}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <FindingLink
                          id={finding.id}
                          finding={finding}
                          onOpen={(item) => onSelectFinding(item, true)}
                        />
                        {finding.package && (
                          <span className="font-mono text-[11px] text-muted">
                            {finding.package}
                            {finding.version ? `@${finding.version}` : ""}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap">{finding.domain}</td>
                    <td className="whitespace-nowrap">{finding.owner || "—"}</td>
                    <td className="whitespace-nowrap">{finding.sla || "—"}</td>
                    <td className="whitespace-nowrap text-right font-semibold tabular-nums text-text">
                      {finding.score ?? "—"}
                    </td>
                    <td className="text-right">
                      <ViewEvidenceButton onClick={() => onSelectFinding(finding, true)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4">
            <EmptyPanel demo={demo} detail="No priority-evidence findings in this assessment." />
          </div>
        )}
      </SectionPanel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Portfolio posture
// ---------------------------------------------------------------------------

export function PortfolioView({
  assessment,
  demo,
  repositories,
  selectedRepoId,
  onSelectRepo,
  onSelectView,
}: {
  assessment: AssuranceAssessment;
  demo: boolean;
  repositories: AssuranceRepository[];
  selectedRepoId?: string;
  onSelectRepo?: (repoId: string) => void;
  onSelectView: (view: string, params?: Record<string, string>) => void;
}) {
  const summary = selectFindingsSummary(assessment);

  // Demo datasets assess a single application; live mode lists the portfolio.
  const rows: AssuranceRepository[] = repositories.length
    ? repositories
    : assessment.repository
      ? [
          {
            ...assessment.repository,
            openFindings: summary.open ?? summary.total,
            openCritical:
              summary.bySeverity.critical ?? summary.bySeverity.Critical,
            lastScanAt: assessment.generatedAt,
          },
        ]
      : [];

  const critical = rows.filter((repo) => (repo.openCritical ?? 0) > 0).length;
  const stale = rows.filter(
    (repo) => freshnessTone(repo.lastScanAt) === "critical"
  ).length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MetricTile label="Applications" value={fmtNumber(rows.length)} demo={demo} />
        <MetricTile
          label="With critical exposure"
          value={fmtNumber(critical)}
          tone={critical ? "critical" : "success"}
          demo={demo}
        />
        <MetricTile
          label="Stale evidence"
          value={fmtNumber(stale)}
          sub="older than 30 days"
          tone={stale ? "warning" : "success"}
          demo={demo}
        />
        <MetricTile
          label="Latest assessment"
          value={relativeTime(assessment.generatedAt)}
          sub={fmtDateTime(assessment.generatedAt)}
          demo={demo}
        />
      </div>

      {demo && (
        <CaveatNote>
          The synthetic dataset assesses a single fictional application. On a live
          deployment this view compares every repository in the portfolio.
        </CaveatNote>
      )}

      <SectionPanel
        title="Application inventory"
        description="Assurance posture per application, scoped to your access"
        flush
      >
        {rows.length ? (
          <div className="overflow-x-auto">
            <table className="asr-table">
              <thead>
                <tr>
                  <th scope="col">Application</th>
                  <th scope="col">Branch</th>
                  <th scope="col" className="text-right">Open findings</th>
                  <th scope="col" className="text-right">Critical</th>
                  <th scope="col">Last assessment</th>
                  <th scope="col">Freshness</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((repo) => {
                  const tone = freshnessTone(repo.lastScanAt);
                  const active = repo.id === selectedRepoId;
                  return (
                    <tr
                      key={repo.id}
                      className={cn(onSelectRepo && "cursor-pointer", active && "bg-brand/5")}
                      onClick={() => onSelectRepo?.(repo.id)}
                    >
                      <td>
                        <div className="font-medium text-text">{repo.name}</div>
                        {repo.organization && (
                          <div className="text-[11px] text-muted">{repo.organization}</div>
                        )}
                      </td>
                      <td className="whitespace-nowrap font-mono text-xs">
                        {repo.defaultBranch || "—"}
                      </td>
                      <td className="whitespace-nowrap text-right font-semibold tabular-nums text-text">
                        {fmtNumber(repo.openFindings) ?? "—"}
                      </td>
                      <td className="whitespace-nowrap text-right">
                        {repo.openCritical ? (
                          <span className="font-semibold tabular-nums text-critical">
                            {fmtNumber(repo.openCritical)}
                          </span>
                        ) : (
                          <span className="tabular-nums text-muted">0</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap">
                        {fmtDateTime(repo.lastScanAt) || (
                          <span className="text-xs text-muted">Awaiting first scan</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap">
                        {repo.lastScanAt ? (
                          <Pill
                            tone={tone}
                            icon={<CalendarClock className="h-3 w-3" aria-hidden="true" />}
                          >
                            {relativeTime(repo.lastScanAt)}
                          </Pill>
                        ) : (
                          <Pill tone="neutral">Not yet scanned</Pill>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4">
            <EmptyPanel
              demo={demo}
              title="No applications in scope"
              detail="The portfolio endpoint returned no repositories your account can access."
            />
          </div>
        )}
      </SectionPanel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Capability evidence
// ---------------------------------------------------------------------------

export function CapabilityView({ assessment, demo }: { assessment: AssuranceAssessment; demo: boolean }) {
  const capabilities = selectCapabilities(assessment);
  const boundary = selectSyntheticBoundary(assessment);
  const backed = capabilities.filter((row) => isEvidenceBacked(row.status)).length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <MetricTile label="Capabilities assessed" value={capabilities.length ? fmtNumber(capabilities.length) : undefined} demo={demo} />
        <MetricTile
          label="Evidence-backed"
          value={capabilities.length ? fmtNumber(backed) : undefined}
          tone="success"
          demo={demo}
        />
        <MetricTile
          label="Requires validation"
          value={capabilities.length ? fmtNumber(capabilities.length - backed) : undefined}
          tone={capabilities.length - backed > 0 ? "warning" : "success"}
          demo={demo}
        />
      </div>

      {boundary.parityCaveat && <CaveatNote tone="warning">{boundary.parityCaveat}</CaveatNote>}

      <SectionPanel
        title="Capability evidence matrix"
        description="Each assurance capability with the evidence that supports it — and its limits"
        flush
      >
        {capabilities.length ? (
          <div className="overflow-x-auto">
            <table className="asr-table">
              <thead>
                <tr>
                  <th scope="col">Capability</th>
                  <th scope="col">Supporting evidence</th>
                  <th scope="col">Status</th>
                  <th scope="col">Limitation</th>
                </tr>
              </thead>
              <tbody>
                {capabilities.map((row, index) => (
                  <tr key={row.capability || index}>
                    <td className="max-w-[220px] font-medium text-text">{row.capability}</td>
                    <td className="max-w-md">{row.evidence || "—"}</td>
                    <td className="whitespace-nowrap">
                      <StatusPill status={row.status} demo={demo} />
                    </td>
                    <td className="max-w-sm text-muted">{row.limitation || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4">
            <EmptyPanel demo={demo} />
          </div>
        )}
      </SectionPanel>
    </div>
  );
}
